import React from 'react';
import { WeatherData } from '../types/community';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CloudSun, Droplets, Wind, ThermometerSun } from 'lucide-react';
import { cn } from './ui/utils';
import { useApp } from '../context/PetParksContext';

interface WeatherWidgetProps {
  weather: WeatherData;
}

export const WeatherWidget: React.FC<WeatherWidgetProps> = ({ weather }) => {
  const { t, language } = useApp();
  const getWalkingScoreColor = (score: string) => {
    switch (score) {
      case 'excellent':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'good':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'fair':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'poor':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return '';
    }
  };

  const getWalkingScoreLabel = (score: string) => {
    switch (score) {
      case 'excellent':
        return t('weatherExcellent');
      case 'good':
        return t('weatherGood');
      case 'fair':
        return t('weatherFair');
      case 'poor':
        return t('weatherPoor');
      default:
        return t('weatherUnknown');
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 dark:from-blue-950 dark:to-cyan-950">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <CloudSun className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold">{t('weatherCurrent')}</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('weatherUpdated')}{' '}
              {new Date(weather.last_updated).toLocaleTimeString(language)}
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-start gap-1">
              <span className="text-3xl font-bold">{weather.temperature}</span>
              <span className="mt-1 text-lg text-muted-foreground">°F</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {weather.conditions}
            </p>
          </div>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2 rounded-md bg-white/50 p-2 dark:bg-black/20">
            <Droplets className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <div>
              <p className="text-xs text-muted-foreground">{t('humidity')}</p>
              <p className="font-medium">{weather.humidity}%</p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-md bg-white/50 p-2 dark:bg-black/20">
            <Wind className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <div>
              <p className="text-xs text-muted-foreground">{t('wind')}</p>
              <p className="font-medium">{weather.wind_speed} mph</p>
            </div>
          </div>
        </div>

        <Badge
          variant="default"
          className={cn(
            'w-full justify-center py-2',
            getWalkingScoreColor(weather.walking_score)
          )}
        >
          <ThermometerSun className="mr-2 h-4 w-4" />
          {getWalkingScoreLabel(weather.walking_score)}
        </Badge>
      </div>
    </Card>
  );
};
